import React,{useState,useCallback,useEffect,useRef} from 'react';
import Icon from '../Text/Icon';
import Subheading from '../Text/Subheading';
import Text from '../Text/Text';
import {parseMermaid,validateMermaid} from '../../helpers/mermaidParser';

const PLACEHOLDER=`flowchart TD
    A((Start)) --> B[Process Data]
    B --> C{Valid?}
    C -->|Yes| D[Save to DB]
    C -->|No| E[Show Error]
    D --> F((End))
    E --> B`;

export default function MermaidImportModal({isOpen,onClose,onImport}) {
    const [mermaidText,setMermaidText]=useState('');
    const [error,setError]=useState(null);
    const [preview,setPreview]=useState(null);
    const textareaRef=useRef(null);

    const handleClose=useCallback(() => {
        setMermaidText('');
        setError(null);
        setPreview(null);
        onClose();
    },[onClose]);

    // Keyboard shortcuts: Escape to close, Cmd/Ctrl+Enter to import
    useEffect(() => {
        if(!isOpen) return;
        const handler=(e) => {
            if(e.key==='Escape') {
                e.preventDefault();
                handleClose();
            }
            if(e.key==='Enter'&&(e.metaKey||e.ctrlKey)&&mermaidText.trim()) {
                e.preventDefault();
                document.getElementById('mermaid-import-btn')?.click();
            }
        };
        window.addEventListener('keydown',handler);
        return () => window.removeEventListener('keydown',handler);
    },[isOpen,handleClose,mermaidText]);

    // Auto-focus textarea when modal opens
    useEffect(() => {
        if(isOpen&&textareaRef.current) {
            setTimeout(() => textareaRef.current?.focus(),100);
        }
    },[isOpen]);

    const handleValidate=useCallback(() => {
        const text=mermaidText.trim();
        if(!text) {
            setError('Please enter Mermaid syntax');
            setPreview(null);
            return false;
        }

        const validation=validateMermaid(text);
        if(!validation.valid) {
            setError(validation.error);
            setPreview(null);
            return false;
        }

        try {
            const result=parseMermaid(text);
            setPreview(result);
            setError(null);
            return true;
        } catch(err) {
            setError(`Parse error: ${err.message}`);
            setPreview(null);
            return false;
        }
    },[mermaidText]);

    const handleImport=useCallback(() => {
        if(!preview) {
            if(!handleValidate()) return;
        }

        const result=preview||parseMermaid(mermaidText.trim());
        onImport(result);
        handleClose();
    },[preview,mermaidText,onImport,handleClose,handleValidate]);

    if(!isOpen) return null;

    return (
        <div
            className='flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50'
            onClick={handleClose}
        >
            <div
                className='bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden m-4 flex flex-col'
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className='flex justify-between items-center p-4 border-b'>
                    <div className='flex gap-2 items-center'>
                        <Icon icon='diagram-3' className='text-xl text-primary-600' />
                        <Subheading className='!mb-0'>Import from Mermaid</Subheading>
                    </div>
                    <button
                        onClick={handleClose}
                        className='p-2 rounded-full transition-colors hover:bg-gray-100'
                        aria-label='Close'
                    >
                        <Icon icon='x' className='text-xl' />
                    </button>
                </div>

                {/* Body */}
                <div className='flex flex-col flex-1 gap-4 p-4 overflow-y-auto'>
                    <Text className='text-sm text-gray-600'>
                        Paste your Mermaid flowchart syntax below. Node shapes will be mapped to Flowsage node types and positions will be calculated automatically.
                    </Text>

                    <div className='flex flex-col gap-1'>
                        <label className='text-xs font-medium text-gray-500 uppercase'>Mermaid Syntax</label>
                        <textarea
                            ref={textareaRef}
                            className='p-3 w-full font-mono text-sm rounded-lg border border-gray-300 transition-colors resize-none focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400'
                            rows={12}
                            value={mermaidText}
                            onChange={(e) => {
                                setMermaidText(e.target.value);
                                setError(null);
                                setPreview(null);
                            }}
                            placeholder={PLACEHOLDER}
                            spellCheck={false}
                        />
                    </div>

                    {error&&(
                        <div className='flex gap-2 items-start p-3 text-sm rounded-lg bg-danger-50 text-danger-700'>
                            <Icon icon='exclamation-triangle' className='flex-shrink-0 mt-0.5' />
                            <span>{error}</span>
                        </div>
                    )}

                    {preview&&(
                        <div className='flex gap-2 items-start p-3 text-sm rounded-lg bg-success-50 text-success-700'>
                            <Icon icon='check-circle' className='flex-shrink-0 mt-0.5' />
                            <span>
                                Valid! Found <strong>{preview.nodes.length}</strong> node{preview.nodes.length!==1? 's':''} and <strong>{preview.edges.length}</strong> edge{preview.edges.length!==1? 's':''}.
                            </span>
                        </div>
                    )}

                    {/* Shape reference */}
                    <details className='text-sm'>
                        <summary className='font-medium text-gray-500 cursor-pointer hover:text-gray-700'>Shape Reference</summary>
                        <div className='grid grid-cols-2 gap-1 mt-2 text-xs text-gray-600'>
                            <div><code className='px-1 bg-gray-100 rounded'>A[text]</code> Rectangle</div>
                            <div><code className='px-1 bg-gray-100 rounded'>A(text)</code> Rounded</div>
                            <div><code className='px-1 bg-gray-100 rounded'>A((text))</code> Circle</div>
                            <div><code className='px-1 bg-gray-100 rounded'>{'A{text}'}</code> Diamond</div>
                            <div><code className='px-1 bg-gray-100 rounded'>{'A{{text}}'}</code> Hexagon</div>
                            <div><code className='px-1 bg-gray-100 rounded'>A[(text)]</code> Cylinder</div>
                            <div><code className='px-1 bg-gray-100 rounded'>{'A>text]'}</code> Arrow</div>
                            <div><code className='px-1 bg-gray-100 rounded'>{'A[/text\\]'}</code> Trapezoid</div>
                        </div>
                    </details>
                </div>

                {/* Footer */}
                <div className='flex gap-3 justify-end p-4 border-t bg-gray-50'>
                    <button
                        onClick={handleValidate}
                        className='px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50'
                    >
                        Validate
                    </button>
                    <button
                        id='mermaid-import-btn'
                        onClick={handleImport}
                        disabled={!mermaidText.trim()}
                        className='px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                        Import Flowchart
                    </button>
                </div>
            </div>
        </div>
    );
}
